// Third Party
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

// UI
import { getTypographyStylesAsClassName } from './typography-styles';

export type TypographyVariant =
  | 'title1'
  | 'title2'
  | 'title3'
  | 'title4'
  | 'title5'
  | 'title6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'small';

export interface ITypographyProps<C extends React.ElementType> {
  /**
   * An override of the default HTML tag.
   * Can also be another React component.
   */
  component?: C;
  /** Applies the theme typography styles */
  variant?: TypographyVariant;
  /** Applies the theme typography styles for medium screens */
  variantM?: TypographyVariant;
  /** Applies the theme typography styles for large screens */
  variantL?: TypographyVariant;
  /** Applies the theme typography styles for xlarge screens */
  variantXL?: TypographyVariant;
  /** Applies the theme typography styles for xxlarge screens */
  variantXXL?: TypographyVariant;
  /** Applies the bold font over the variant */
  bold?: boolean;
}

// export type TypographyProps<C extends React.ElementType> = ITypographyProps<C> &
//   Omit<React.ComponentPropsWithRef<C>, keyof ITypographyProps<C>>;

// Extend Omit to also exclude 'style' and 'className'
export type TypographyProps<C extends React.ElementType> = ITypographyProps<C> &
  Omit<React.ComponentPropsWithRef<C>, keyof ITypographyProps<C> | 'style'>;

export const Typography = <C extends React.ElementType = 'span'>({
  component,
  variant,
  variantM,
  variantL,
  variantXL,
  variantXXL,
  bold,
  children,
  className: propClassName,
  ...rest
}: React.PropsWithChildren<TypographyProps<C>>): JSX.Element | null => {
  const typographyClassName = getTypographyStylesAsClassName(
    variant,
    variantM,
    variantL,
    variantXL,
    variantXXL,
    bold
  );

  const props = {
    ...rest,
    className: twMerge(typographyClassName, propClassName),
  };

  if (typeof children === 'string' || component !== undefined) {
    const Tag = component || 'span';
    return <Tag {...props}>{children}</Tag>;
  }

  if (!children) {
    return null;
  } else if (
    React.Children.count(children) === 1 &&
    React.isValidElement(children)
  ) {
    const child = React.Children.only(children);
    return <child.type {...(child.props as object)} {...props} />;
  } else {
    throw new Error(
      'Typography component expects one children tree or a string'
    );
  }
};
